import { useEffect, useState, type ReactNode } from "react";
import {
  ENV_STATUS_URL,
  currentEnvSlug,
  type EnvStatusArtifact,
  type EnvStatusEntry,
} from "@/config/env-status";
import EnvironmentPausedScreen from "@/components/common/EnvironmentPausedScreen";

const CHECK_TIMEOUT_MS = 2500;

/**
 * When env-power parks the non-prod EMS environment this build targets, render a "paused"
 * screen instead of failing against a stopped API. Reads a small static status artifact
 * from a CloudFront edge (available even when the target API is down).
 *
 * OPTIMISTIC (renders the app immediately) and FAIL-OPEN (any fetch failure leaves the app
 * rendering). INERT on production/unknown builds or when VITE_ENV_STATUS_URL is unset.
 */
export function EnvironmentPausedGate({ children }: { children: ReactNode }) {
  const [pausedEntry, setPausedEntry] = useState<EnvStatusEntry | null>(null);

  const slug = currentEnvSlug();
  const gateApplies = Boolean(ENV_STATUS_URL) && slug !== null;

  useEffect(() => {
    if (!gateApplies || !slug) return;
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch(ENV_STATUS_URL as string, {
          cache: "no-store",
          signal: AbortSignal.timeout(CHECK_TIMEOUT_MS),
        });
        if (!res.ok) return;
        const data = (await res.json()) as EnvStatusArtifact;
        const entry = data.environments?.find((e) => e.environment === slug);
        if (!cancelled && entry && (entry.status === "paused" || entry.status === "starting")) {
          setPausedEntry(entry);
        }
      } catch {
        // Fail open — never block the site on a status-check failure.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gateApplies, slug]);

  if (pausedEntry) return <EnvironmentPausedScreen entry={pausedEntry} />;
  return <>{children}</>;
}
